import React from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";

const COLUMNS = {
  checkbox: 40,
  tipo: 70,
  nivel_grado: 100,
  area: 140,
  profesor: 110,
  fecha: 90,
  sesion: 200,
  descripcion: 240,
  acciones: 140,
};

const obtenerContexto = (texto, busqueda) => {
  if (!busqueda || !texto) return texto;

  const busquedaEscapada = busqueda.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regexStr = busquedaEscapada
    .replace(/[aáAÁ]/g, '[aáAÁ]')
    .replace(/[eéEÉ]/g, '[eéEÉ]')
    .replace(/[iíIÍ]/g, '[iíIÍ]')
    .replace(/[oóOÓ]/g, '[oóOÓ]')
    .replace(/[uúüUÚÜ]/g, '[uúüUÚÜ]');

  const regex = new RegExp(regexStr, 'i');
  const match = texto.match(regex);

  // Si no encuentra la palabra en la descripción (quizás estaba en el título), devuelve el texto normal
  if (!match) return texto;

  const index = match.index;
  // Cortamos unos 40 caracteres antes y 60 después para dar contexto
  let start = Math.max(0, index - 40);
  let end = Math.min(texto.length, index + busqueda.length + 60);

  let resultado = texto.substring(start, end);

  // Añadimos puntos suspensivos para indicar que el texto está cortado
  if (start > 0) resultado = "..." + resultado;
  if (end < texto.length) resultado = resultado + "...";

  return resultado;
};

// === FUNCIÓN PARA RESALTAR TEXTO ===
const resaltarTexto = (texto, busqueda) => {
  if (!busqueda || !texto) return texto;

  // Escapar caracteres especiales de regex
  const busquedaEscapada = busqueda.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Reemplazar vocales para ignorar tildes
  const regexStr = busquedaEscapada
    .replace(/[aáAÁ]/g, '[aáAÁ]')
    .replace(/[eéEÉ]/g, '[eéEÉ]')
    .replace(/[iíIÍ]/g, '[iíIÍ]')
    .replace(/[oóOÓ]/g, '[oóOÓ]')
    .replace(/[uúüUÚÜ]/g, '[uúüUÚÜ]');

  // Crear expresión regular global e insensible a mayúsculas
  const regex = new RegExp(`(${regexStr})`, 'gi');

  // Dividir el texto. Al usar grupos de captura (), las coincidencias caen en los índices impares
  const partes = String(texto).split(regex);

  return partes.map((parte, index) =>
    index % 2 === 1 ? (
      <span key={index} style={{ backgroundColor: "#fff59d", padding: "0 2px", borderRadius: "3px", color: "#000" }}>
        {parte}
      </span>
    ) : (
      parte
    )
  );
};

const SesionTable = ({
  /* data */
  data = [],
  loading = false,
  total = 0,
  page = 0,
  rowsPerPage = 10,

  /* Recibimos la búsqueda para saber qué resaltar */
  search = "",

  /* PROPS: Para la cabecera dinámica */
  isMobile = false,
  onOpenFilters = () => { },

  /* selección */
  selectedIds = [],
  allSelected = false,
  someSelected = false,

  /* permisos */
  canSelect = false,
  canDelete = false,
  canGenerateSesion = () => false,
  canSelectRow = () => false,

  /* acciones */
  onRowClick = () => { },
  onToggleRow = () => { },
  onToggleAll = () => { },
  onDeleteSelected = () => { },
  onGenerate = () => { },
  onView = () => { },
  onDownload = () => { },
  onClearFilters = () => { },

  /* estado */
  generacionActiva = false,

  /* paginación */
  onPageChange,
  onRowsPerPageChange,
  maxTableHeight = "calc(100vh - 220px)",
  tableHeight,

}) => {
  const colSpan = canSelect ? 9 : 8;
  const [openDescripcion, setOpenDescripcion] = React.useState(false);
  const [descripcionActiva, setDescripcionActiva] = React.useState(null);
  const emptyStateMinHeight = `calc(${maxTableHeight} - 52px)`;

  // Cálculos para el texto de paginación
  const inicio = total === 0 ? 0 : page * rowsPerPage + 1;
  const fin = Math.min((page + 1) * rowsPerPage, total);

  const abrirDescripcion = (sesion) => {
    setDescripcionActiva(sesion);
    setOpenDescripcion(true);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: tableHeight || "auto",
        overflowX: { xs: "auto", md: "hidden" },
        borderRadius: "12px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
      }}
    >
      <Paper
        className="tabla-paper"
        sx={{
          overflow: "hidden",
          borderRadius: "12px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >

        {/* === CABECERA DE LA TABLA === */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: selectedIds.length > 0 ? "1px solid rgba(255,65,65,0.3)" : "none",
            backgroundColor: selectedIds.length > 0 ? "rgba(255, 65, 65, 0.08)" : "#9f9f9f",
            transition: "background-color 0.3s ease",
            minHeight: "56px"
          }}
        >
          {selectedIds.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ color: "#d32f2f", fontWeight: "bold" }}>
                {selectedIds.length} sesión(es) seleccionada(s)
              </Typography>

              {canDelete && (
                <IconButton
                  onClick={onDeleteSelected}
                  sx={{ color: "#d32f2f" }}
                  title="Eliminar seleccionados"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </>
          ) : (
            <>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "var(--white)",
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.875rem" }
                }}
              >
                Mostrando {inicio} al {fin} de {total} sesiones
              </Typography>

              {isMobile && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FilterListIcon />}
                  onClick={onOpenFilters}
                  sx={{
                    borderRadius: "8px",
                    color: "var(--white)",
                    borderColor: "var(--border)",
                    "&:hover": {
                      borderColor: "var(--white)",
                      backgroundColor: "rgba(255, 255, 255, 0.15)"
                    }
                  }}
                >
                  Filtros
                </Button>
              )}
            </>
          )}
        </Box>

        {/* === TABLA COMPACTA === */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <TableContainer sx={{ maxHeight: maxTableHeight, height: "100%", overflowY: "auto", overflowX: "auto" }}>
            <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      py: 1,
                      px: 1,
                      backgroundColor: "#9f9f9f",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: "#ffffff",
                      borderBottom: "2px solid #8f8f8f"
                    },
                  }}
                >
                  {canSelect && (
                    <TableCell sx={{ width: COLUMNS.checkbox }} padding="checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={!data.some((sesion) => canSelectRow(sesion))}
                        ref={(el) => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={onToggleAll}
                      />
                    </TableCell>
                  )}

                  <TableCell sx={{ width: COLUMNS.tipo, textAlign: "center" }}>Tipo</TableCell>
                  <TableCell sx={{ width: COLUMNS.nivel_grado, textAlign: "center" }}>Grado / Nivel</TableCell>
                  <TableCell sx={{ width: COLUMNS.area, display: { xs: "none", md: "table-cell" }, textAlign: "center" }}>Área</TableCell>
                  <TableCell sx={{ width: COLUMNS.profesor, display: { xs: "none", md: "table-cell" }, textAlign: "center" }}>Docente</TableCell>
                  <TableCell sx={{ width: COLUMNS.fecha, display: { xs: "none", sm: "table-cell" }, textAlign: "center" }}>Fecha</TableCell>
                  <TableCell sx={{ width: COLUMNS.sesion }}>Unidad/Sesión</TableCell>
                  <TableCell sx={{ width: COLUMNS.descripcion, display: { xs: "none", sm: "table-cell" } }}>Descripción</TableCell>
                  <TableCell sx={{ width: COLUMNS.acciones, textAlign: "center" }}>Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 3 }}>
                      Cargando resultados…
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow
                    className="empty-state-row"
                    hover={false}
                    sx={{
                      "&:hover": {
                        backgroundColor: "transparent !important",
                      },
                    }}
                  >
                    <TableCell colSpan={colSpan} sx={{ p: 0, borderBottom: "none" }}>
                      <Box
                        sx={{
                          minHeight: emptyStateMinHeight,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1.5,
                          color: "text.secondary",
                          px: 3,
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.primary" }}>
                          No se encontraron resultados
                        </Typography>

                        <Typography variant="body2" sx={{ maxWidth: 420 }}>
                          Intenta ajustar tu búsqueda o limpiar los filtros aplicados para volver a ver sesiones disponibles.
                        </Typography>

                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={onClearFilters}
                          sx={{ textTransform: "none", fontWeight: 600, mt: 0.5 }}
                        >
                          Limpiar filtros
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((sesion) => {
                    const isSelected = selectedIds.includes(sesion.id);
                    const canDeleteThisRow = Boolean(canSelectRow(sesion));
                    const canSelectThisRow = canSelect && canDeleteThisRow;
                    const fecha = sesion.created_at
                      ? new Date(sesion.created_at).toLocaleDateString("es-PE")
                      : "";

                    const selectableCellProps = canSelectThisRow
                      ? {
                        onClick: () => onToggleRow(sesion.id),
                        sx: { px: 1, py: 0.5 },
                      }
                      : { sx: { px: 1, py: 0.5 } };

                    return (
                      <TableRow
                        key={sesion.id}
                        hover
                        selected={canSelectThisRow && isSelected}
                        sx={{
                          cursor: canSelectThisRow ? "pointer" : "default",
                          "& td": { borderBottom: "1px solid #f0f0f0" },
                          "&:hover": {
                            backgroundColor: "rgba(15, 23, 42, 0.018)",
                          },
                        }}
                      >
                        {canSelect && (
                          <TableCell padding="checkbox" {...selectableCellProps}>
                            <Tooltip
                              title={
                                canDeleteThisRow
                                  ? ""
                                  : "No tienes permisos para eliminar esta sesión"
                              }
                              arrow
                              placement="top"
                            >
                              <span>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={!canDeleteThisRow}
                                  onClick={(e) => e.stopPropagation()}
                                  onChange={() => {
                                    if (canSelectThisRow) {
                                      onToggleRow(sesion.id);
                                    }
                                  }}
                                  style={{
                                    cursor: canDeleteThisRow ? "pointer" : "not-allowed",
                                    opacity: canDeleteThisRow ? 1 : 0.45,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}

                        <TableCell {...selectableCellProps} sx={{ textAlign: "center", fontSize: "0.75rem" }}>
                          {sesion.tipo_sesion}
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ textAlign: "center" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.8rem", color: "#333" }}>
                              {sesion.grado}°
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: "0.8rem", color: "text.secondary", textTransform: "capitalize" }}>
                              {sesion.nivel?.toLowerCase()}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ ...selectableCellProps.sx, display: { xs: "none", md: "table-cell" }, textAlign: "center", fontSize: "0.75rem" }}>
                          {sesion.area_tematica}
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ ...selectableCellProps.sx, display: { xs: "none", md: "table-cell" }, textAlign: "center", fontSize: "0.75rem" }}>
                          {sesion.usuario?.nombres ? sesion.usuario.nombres.split(" ")[0] : "Sin nombre"}
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ ...selectableCellProps.sx, display: { xs: "none", sm: "table-cell" }, textAlign: "center", fontSize: "0.75rem" }}>
                          {fecha}
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ ...selectableCellProps.sx, fontSize: "0.8rem", fontWeight: 500 }}>
                          {/* APLICAMOS EL RESALTADO AL TEMA */}
                          {sesion.tipo_sesion === "SAC" && (sesion.nro_unidad != null || sesion.nro_sesion != null) && (
                            <>
                              {sesion.nro_unidad != null && <>Unidad {sesion.nro_unidad} </>}
                              {sesion.nro_sesion != null && <>Sesión {sesion.nro_sesion}</>}
                              {": "}
                            </>
                          )}
                          {resaltarTexto(sesion.tema, search)}
                        </TableCell>

                        <TableCell {...selectableCellProps} sx={{ ...selectableCellProps.sx, display: { xs: "none", sm: "table-cell" } }}>
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.75rem",
                                color: "text.secondary",
                                display: "-webkit-box",
                                WebkitLineClamp: 4,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {/* APLICAMOS EL SNIPPET ANTES DE RESALTAR */}
                              {resaltarTexto(obtenerContexto(sesion.objetivo || "Sin descripción", search), search)}
                            </Typography>

                            {sesion.objetivo?.length > 90 && (
                              <Button
                                size="small"
                                sx={{ px: 0, minWidth: "auto", fontSize: "0.65rem", mt: -0.5 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  abrirDescripcion(sesion);
                                }}
                              >
                                Ver más
                              </Button>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ px: 2, py: 2, cursor: "default" }}>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, justifyContent: "center" }}>
                            {canGenerateSesion(sesion) ? (
                              <Button
                                variant="contained"
                                color="primary"
                                disabled={generacionActiva}
                                onClick={() => onGenerate(sesion)}
                                sx={{ textTransform: "none", fontWeight: 600, py: 0.5 }}
                              >
                                Generar
                              </Button>
                            ) : (
                              <>
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={() => onView(sesion)}
                                  sx={{ textTransform: "none", fontWeight: 600, py: 0.5, color: "white" }}
                                >
                                  Ver PDF
                                </Button>

                                <Button
                                  variant="outlined"
                                  color="primary"
                                  startIcon={<DownloadIcon />}
                                  onClick={() => onDownload(sesion)}
                                  sx={{ textTransform: "none", fontWeight: 600, py: 0.5 }}
                                >
                                  Descargar
                                </Button>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10) || 25)}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas:"
          sx={{ borderTop: "1px solid #e0e0e0" }}
        />

        {/* === MODAL DE DESCRIPCIÓN === */}
        <Dialog open={openDescripcion} onClose={() => setOpenDescripcion(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontSize: 18, fontWeight: 600, borderBottom: "1px solid #ddd", pb: 1 }}>

            {resaltarTexto(descripcionActiva?.tema, search)}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ lineHeight: 1.7, color: "#444", whiteSpace: "pre-line", textAlign: "justify" }}>

              {resaltarTexto(descripcionActiva?.objetivo, search)}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ borderTop: "1px solid #ddd", px: 3, py: 1, justifyContent: "flex-end" }}>
            <Button onClick={() => setOpenDescripcion(false)}>CERRAR</Button>
          </DialogActions>
        </Dialog>

      </Paper>
    </Box>
  );
};

export default SesionTable;
